import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { setupRouterGuards } from './guards'
import { getLoginRoute, isClient, isAdmin } from '@/utils/app-config'

/**
 * 获取认证路由的子路由配置
 */
function getAuthChildren(): RouteRecordRaw[] {
  const routes: RouteRecordRaw[] = []

  // 客户端认证路由
  if (isClient()) {
    routes.push({
      path: 'portal',
      name: 'PortalAuth',
      component: () => import('@/views/auth/portal.vue'),
      meta: {
        title: '用户登录',
        hidden: true
      }
    })
  }

  // 租户端认证路由 - 包含 staff 和 tenant 两个路由
  if (isAdmin()) {
    routes.push(
      {
        path: 'staff',
        name: 'StaffAuth',
        component: () => import('@/views/auth/staff.vue'),
        meta: {
          title: '员工登录',
          hidden: true
        }
      },
      {
        path: 'tenant',
        name: 'TenantAuth',
        component: () => import('@/views/auth/tenant-login.vue'),
        meta: {
          title: '租户登录',
          hidden: true
        }
      }
    )
    if (import.meta.env.DEV) {
      routes.push({
        path: 'dev-token',
        name: 'DevTokenAuth',
        component: () => import('@/views/auth/dev-token-login.vue'),
        meta: {
          title: '开发Token登录',
          hidden: true
        }
      })
    }
  }

  return routes
}

/**
 * 获取SSO路由的子路由配置
 */
function getSSOChildren(): RouteRecordRaw[] {
  return [
    {
      path: 'auth/:provider',
      name: 'SSOAuth',
      component: () => import('@/views/auth/sso-auth.vue'),
      meta: {
        title: 'SSO授权绑定',
        hidden: true
      }
    }
  ]
}

/**
 * 匿名页面
 */
function getAnonymousAccess(): RouteRecordRaw[] {
  return [
    {
      path: 'quotation/detail',
      name: 'QuotationDetail',
      component: () => import('@/views/anonymous-access/quotation/detail.vue'),
      meta: {
        title: '报价单详情',
        hidden: true
      }
    },
    {
      path: 'quotation-express/detail',
      name: 'QuotationExpressDetail',
      component: () => import('@/views/anonymous-access/quotation-express/detail.vue'),
      meta: {
        title: '快递报价单详情',
        hidden: true
      }
    },
    {
      path: 'custom-quotation/quotation-sea/detail',
      name: 'CustomQuotationSeaDetail',
      component: () => import('@/views/anonymous-access/custom-quotation/quotation-sea/detail.vue'),
      meta: {
        title: '海运报价单详情',
        hidden: true
      }
    },
    {
      path: 'custom-quotation-v2/quotation-sea/detail',
      name: 'CustomQuotationSeaDetailV2',
      component: () => import('@/views/anonymous-access/custom-quotation-v2/quotation-sea/detail.vue'),
      meta: {
        title: '海运询价单',
        hidden: true
      }
    },
    {
      path: 'custom-quotation/quotation-air/detail',
      name: 'CustomQuotationAirDetail',
      component: () => import('@/views/anonymous-access/custom-quotation/quotation-air/detail.vue'),
      meta: {
        title: '空运报价单详情',
        hidden: true
      }
    },
    {
      path: 'custom-quotation-v2/quotation-air/detail',
      name: 'CustomQuotationAirDetailV2',
      component: () => import('@/views/anonymous-access/custom-quotation-v2/quotation-air/detail.vue'),
      meta: {
        title: '空运询价单',
        hidden: true
      }
    },
    {
      path: 'custom-quotation-v2/quotation-rail/detail',
      name: 'CustomQuotationRailDetailV2',
      component: () => import('@/views/anonymous-access/custom-quotation-v2/quotation-rail/detail.vue'),
      meta: {
        title: '铁路询价单',
        hidden: true
      }
    },
    {
      path: 'custom-quotation-v2/quotation-pre-port/detail',
      name: 'CustomQuotationPrePortDetailV2',
      component: () => import('@/views/anonymous-access/custom-quotation-v2/quotation-pre-port/detail.vue'),
      meta: {
        title: '港前询价单',
        hidden: true
      }
    },
    {
      path: 'custom-quotation/quotation-rail/detail',
      name: 'CustomQuotationRailDetail',
      component: () => import('@/views/anonymous-access/custom-quotation/quotation-rail/detail.vue'),
      meta: {
        title: '铁路报价单详情',
        hidden: true
      }
    },
    {
      path: 'custom-quotation/quotation-pre-port/detail',
      name: 'CustomQuotationPrePortDetail',
      component: () => import('@/views/anonymous-access/custom-quotation/quotation-pre-port/detail.vue'),
      meta: {
        title: '港前报价单详情',
        hidden: true
      }
    },
    {
      path: 'custom-quotation/quotation-last-mile/detail',
      name: 'CustomQuotationLastMileDetail',
      component: () => import('@/views/anonymous-access/custom-quotation/quotation-last-mile/detail.vue'),
      meta: {
        title: '尾程报价单详情',
        hidden: true
      }
    },
    {
      path: 'custom-quotation-v2/quotation-last-mile/detail',
      name: 'CustomQuotationLastMileDetailV2',
      component: () => import('@/views/anonymous-access/custom-quotation-v2/quotation-last-mile/detail.vue'),
      meta: {
        title: '尾程询价单',
        hidden: true
      }
    },
    {
      path: 'custom-quotation/quotation-express/detail',
      name: 'CustomQuotationExpressDetail',
      component: () => import('@/views/anonymous-access/custom-quotation/quotation-express/detail.vue'),
      meta: {
        title: '快递报价单详情',
        hidden: true
      }
    },
    {
      path: 'shokz-quotation/detail',
      name: 'ShokzQuotationDetail',
      component: () => import('@/views/anonymous-access/shokz-quotation/detail.vue'),
      meta: {
        title: '报价单详情',
        hidden: true
      }
    },
    {
      path: 'sales-quotation/detail',
      name: 'SalesQuotationLinkDetail',
      component: () => import('@/views/anonymous-access/sales-quotation/detail.vue'),
      meta: {
        title: '销售报价详情',
        hidden: true,
        requiresAuth: false
      }
    }
  ]
}

// 基础路由（不需要权限的路由）
const devTokenShortcut: RouteRecordRaw[] =
  import.meta.env.DEV && isAdmin()
    ? [{ path: '/dev-token', redirect: '/auth/dev-token', meta: { hidden: true } }]
    : []

/** 联调文档曾写 /system/api-sync-settings，实际路由在 API 中心下 */
const apiSyncSettingShortcut: RouteRecordRaw[] = [
  {
    path: '/system/api-sync-settings',
    redirect: '/api-center/api-sync-settings?tab=docControl',
    meta: { hidden: true }
  }
]

const baseRoutes: RouteRecordRaw[] = [
  ...devTokenShortcut,
  ...apiSyncSettingShortcut,
  {
    path: '/bidding/supplier/bid/:tenderId',
    name: 'SupplierBid',
    component: () => import('@/views/bidding/supplier-bid/index.vue'),
    meta: {
      title: '应标报价',
      hidden: true,
      requiresAuth: false
    }
  },

  {
    path: '/supplier-quotation/:token',
    name: 'SupplierQuotationH5',
    component: () => import('@/views/system-management/supplier-quotation-config/supplier-h5.vue'),
    meta: {
      title: '供应商报价',
      hidden: true,
      requiresAuth: false
    }
  },
  {
    path: '/auth',
    name: 'Auth',
    redirect: () => getLoginRoute(),
    meta: {
      title: '认证'
    },
    children: getAuthChildren()
  },
  // SSO授权页面路由
  {
    path: '/sso',
    name: 'SSO',
    meta: {
      title: 'SSO授权',
      hidden: true
    },
    children: getSSOChildren()
  },

  // 404页面
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/404.vue'),
    meta: {
      title: 'error.404.title',
      hidden: true
    }
  },
  // 暂无权限访问页面（无任何有权菜单时展示）
  {
    path: '/no-permission',
    name: 'NoPermission',
    component: () => import('@/views/no-permission.vue'),
    meta: {
      title: '暂无权限访问',
      hidden: true
    }
  },
  // 允许匿名访问的 链接
  {
    path: '/anonymous-access',
    name: 'AnonymousAccess',
    meta: {
      title: '匿名访问页面',
      hidden: true
    },
    children: getAnonymousAccess()
  },

  {
    path: '/supplier/space-booking/:bookingId',
    name: 'SupplierSpaceBooking',
    component: () => import('@/views/order/transport-consignment/SpaceBookingExternal.vue'),
    meta: {
      title: '询舱确认',
      hidden: true,
      requiresAuth: false
    }
  },
  {
    path: '/external/consignment/:consignmentNo',
    name: 'ExternalConsignmentConfirm',
    component: () => import('@/views/order/transport-consignment/ConsignmentConfirmExternal.vue'),
    meta: {
      title: '委托确认',
      hidden: true,
      requiresAuth: false
    }
  },

  // 独立页面路由 - 运踪查询（不使用侧边栏和顶部导航）
  {
    path: '/tracking-query',
    name: 'TrackingQueryStandalone',
    component: () => import('@/layouts/StandaloneLayout.vue'),
    meta: {
      title: '运踪查询',
      hidden: true
    },
    children: [
      {
        path: '',
        name: 'TrackingQueryStandalonePage',
        component: () => import('@/views/tools/tracking-query/index.vue'),
        meta: {
          title: '运踪查询',
          requiresAuth: false
        }
      }
    ]
  },

  // 韶音 BPM 整页审批（Standalone 裸页，需登录；未加入 baseRoutePaths 免登白名单）
  {
    path: '/saas/shokz-inquiry-bpm/:type/:id',
    component: () => import('@/layouts/StandaloneLayout.vue'),
    meta: {
      title: '询价BPM审批',
      hidden: true
    },
    children: [
      {
        path: '',
        name: 'ShokzInquiryBpmApproval',
        component: () => import('@/views/saas/shokz-inquiry-management/bpm-approval/index.vue'),
        props: true,
        meta: {
          title: '询价BPM审批',
          requiresAuth: false,
          hidden: true
        }
      }
    ]
  },
  // 对账（tc-fee）整页审批：独立页面组件，与询价 BPM 解耦；静态路由先于 :businessType 命中
  {
    path: '/bpm/approval/tc-fee',
    component: () => import('@/layouts/StandaloneLayout.vue'),
    meta: {
      title: '对账BPM审批',
      hidden: true
    },
    children: [
      {
        path: '',
        name: 'TcFeeBpmApproval',
        component: () => import('@/views/bpm/tc-fee-approval/index.vue'),
        props: true,
        meta: {
          title: '对账BPM审批',
          requiresAuth: false,
          hidden: true
        }
      }
    ]
  },
  // 特价申请整页审批：独立页面组件，与询价 BPM 解耦；静态路由先于 :businessType 命中
  {
    path: '/bpm/approval/special-price',
    component: () => import('@/layouts/StandaloneLayout.vue'),
    meta: {
      title: '特价申请BPM审批',
      hidden: true
    },
    children: [
      {
        path: '',
        name: 'SpecialPriceBpmApproval',
        component: () => import('@/views/bpm/special-price-approval/index.vue'),
        props: true,
        meta: {
          title: '特价申请BPM审批',
          requiresAuth: false,
          hidden: true
        }
      }
    ]
  },
  // 兼容原型调试 URL：/special-price-approval/detail/:id
  {
    path: '/special-price-approval/detail/:id',
    component: () => import('@/layouts/StandaloneLayout.vue'),
    meta: {
      title: '特价申请BPM审批',
      hidden: true
    },
    children: [
      {
        path: '',
        name: 'SpecialPriceApprovalDetail',
        component: () => import('@/views/bpm/special-price-approval/index.vue'),
        props: true,
        meta: {
          title: '特价申请BPM审批',
          requiresAuth: false,
          hidden: true
        }
      }
    ]
  },
  {
    path: '/bpm/approval/settlement-bill',
    component: () => import('@/layouts/StandaloneLayout.vue'),
    meta: {
      title: '账单审批申请',
      hidden: true
    },
    children: [
      {
        path: '',
        name: 'SettlementBillBpmApproval',
        component: () => import('@/views/bpm/settlement-bill-approval/index.vue'),
        props: true,
        meta: {
          title: '账单审批申请',
          requiresAuth: false,
          hidden: true
        }
      }
    ]
  },
  // 运输委托单费用更改审批整页：主区域展示申请前后费用快照，右侧沿用通用审批操作面板。
  {
    path: '/bpm/approval/transport-consignment-fee-change',
    component: () => import('@/layouts/StandaloneLayout.vue'),
    meta: {
      title: '费用更改审批',
      hidden: true
    },
    children: [
      {
        path: '',
        name: 'TransportConsignmentFeeChangeBpmApproval',
        component: () => import('@/views/bpm/transport-consignment-fee-change-approval/index.vue'),
        props: true,
        meta: {
          title: '费用更改审批',
          requiresAuth: false,
          hidden: true
        }
      }
    ]
  },
  {
    path: '/bpm/approval/:businessType',
    component: () => import('@/layouts/StandaloneLayout.vue'),
    meta: {
      title: '询价BPM审批',
      hidden: true
    },
    children: [
      {
        path: '',
        name: 'ShokzInquiryBpmApprovalByBusiness',
        component: () => import('@/views/saas/shokz-inquiry-management/bpm-approval/index.vue'),
        props: true,
        meta: {
          title: '询价BPM审批',
          requiresAuth: false,
          hidden: true
        }
      }
    ]
  }
]
// const appType = process.env.VITE_APP_TYPE || 'client'
const baseUrl = isClient() ? '/client/' :'/'
// 创建路由实例（初始只包含基础路由）
const router = createRouter({
  history: createWebHistory(baseUrl),
  routes: baseRoutes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  }
})

// 注意：权限检查和动态路由添加逻辑已移至 guards.ts 中统一处理

// 设置路由守卫（包含权限检查和动态路由添加逻辑）
setupRouterGuards(router)

// 处理动态模块加载失败的错误
router.onError((error, to) => {
  const errMsg = error?.message || String(error || '')
  const isChunkLoadError =
    errMsg.includes('Failed to fetch dynamically imported module') ||
    errMsg.includes('Importing a module script failed') ||
    errMsg.includes('Loading chunk') ||
    errMsg.includes('Loading CSS chunk')

  if (isChunkLoadError) {
    console.warn('⚠️ 检测到动态模块加载失败，即将刷新页面:', error.message)

    // 避免无限刷新循环
    const lastReload = sessionStorage.getItem('lastChunkErrorReload')
    const now = Date.now()

    if (!lastReload || now - parseInt(lastReload) > 30000) {
      // 30秒内不重复刷新
      sessionStorage.setItem('lastChunkErrorReload', now.toString())

      if (to?.fullPath) {
        window.location.href = to.fullPath
      } else {
        window.location.reload()
      }
    } else {
      console.error('💀 检测到频繁的模块加载错误，停止自动刷新')
    }
  }
})

// 权限相关方法已移至 guards.ts 中，可通过以下方式导入：
// import { userPermissions, hasPermission, resetPermissionState } from './guards'

export default router
